/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { BarItemData } from '#UNSAFE_BarChart/barChart.types';
import { ChartSeriesData } from '#utils/UNSAFE_visTypes/chart';

const IDEAL_DATA_ITEM_GAP = 2;
const DEFAULT_BAR_GAP_RATIO = 0.25;

/**
 * The function that calculates the barWidth and offset from group center for each series.
 * @param axisStepWidth The width of the group.
 * @param noOfSeries The number of series present in the chart.
 * @param barGapRatio The ratio of gap to axisStepWidth.
 * @param dataItemGaps The ratio that determines the gap between the bars.
 */
export function getBarGapsInfo<K extends string | number, D extends BarItemData<K>>(
  seriesIndex: number,
  groupIndex: number,
  series: ChartSeriesData<string | number, any>,
  axisStepWidth: number,
  averageGroupZ: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  barGapRatio: number,
  numSeriesY: number,
  numSeriesY2: number,
  dataItemGaps: number,
  isStacked: boolean,
  isRtl: boolean,
  isHoriz: boolean
) {
  const isY2 = series.associatedYAxis === 'y2';
  const numSeries = isStacked ? 1 : isY2 ? numSeriesY2 : numSeriesY;
  const itemsGaps = IDEAL_DATA_ITEM_GAP * dataItemGaps;
  const item = getDataItem(seriesIndex, groupIndex);
  const isVariableBarWidth = item?.widthWeight !== undefined;
  const barWidth = isVariableBarWidth
    ? getBarWidth(seriesIndex, groupIndex, axisStepWidth, averageGroupZ, getDataItem)
    : (axisStepWidth * (1 - barGapRatio) - (numSeries - 1) * itemsGaps) / numSeries;
  if (isStacked) {
    return { barWidth, offset: (-1 * barWidth) / 2, itemsGaps };
  }
  const center = axisStepWidth / 2;
  const seriesStart =
    axisStepWidth * (barGapRatio / 2) +
    seriesIndex * (barWidth + IDEAL_DATA_ITEM_GAP * dataItemGaps);
  const offset = getOffset(
    seriesIndex,
    groupIndex,
    getDataItem,
    averageGroupZ,
    axisStepWidth,
    barWidth,
    numSeries,
    seriesStart,
    center,
    isRtl,
    isHoriz,
    isVariableBarWidth
  );

  return { barWidth, offset, itemsGaps };
}

/**
 * Returns the bar gap ratio.
 * @param isStacked Whether the chart is a stacked bar chart.
 * @param startIndex The viewport start index.
 * @param endIndex The viewport end index.
 */
export function getBarGapRatio(isStacked: boolean, startIndex: number, endIndex: number) {
  const noOfGroups = endIndex - startIndex + 1;
  // logic from legacy
  return isStacked ? 0.37 + 0.26 / noOfGroups : DEFAULT_BAR_GAP_RATIO;
}

export function getBarWidth<K extends string | number, D extends BarItemData<K>>(
  seriesIndex: number,
  groupIndex: number,
  axisStepWidth: number,
  averageGroupZ: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined
) {
  const widthWeightValue = getWidthWeightVal(getDataItem, seriesIndex, groupIndex, 1);
  const ratio = widthWeightValue / averageGroupZ;
  return ratio * axisStepWidth;
}

export function getOffset<K extends string | number, D extends BarItemData<K>>(
  seriesIndex: number,
  groupIndex: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  averageGroupZ: number,
  axisStepWidth: number,
  barWidth: number,
  numSeries: number,
  seriesStart: number,
  center: number,
  isRtl: boolean,
  isHoriz: boolean,
  isVariableBarWidth: boolean
) {
  let yTotalWidth = 0;
  const offset = [];
  if (isVariableBarWidth) {
    for (let s = 0; s < numSeries; s++) {
      offset[s] = yTotalWidth;
      yTotalWidth += getBarWidth(s, groupIndex, axisStepWidth, averageGroupZ, getDataItem);
    }
    for (const s in offset) {
      offset[s] -= yTotalWidth / 2;
    }
    return offset[seriesIndex];
  } else {
    return isRtl && !isHoriz ? center - seriesStart - barWidth : seriesStart - center;
  }
}

export function getWidthWeightVal<K extends string | number, D extends BarItemData<K>>(
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  seriesIndex: number,
  groupIndex: number,
  defaultVal: number
) {
  const dataItem = getDataItem(seriesIndex, groupIndex);
  if (dataItem == null || typeof dataItem != 'object') return defaultVal;
  if (dataItem['widthWeight'] != null)
    // Object with value property
    return Math.max(0, dataItem['widthWeight']); // override any negative widthWeight values as 0
  return defaultVal;
}
